import * as Sentry from "@sentry/react";

import { IS_AWS_ENV } from "constants/app.constants";
import {
  PREDICATE_PROPERTY_OPERATOR,
  PREDICATE_TYPE,
  SUBTRACTIVE_OPERATORS
} from "constants/filter.constants";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import {
  RigFracFocusAttribute,
  getFocusSyncTrigger
} from "utils/color/focusColorSync/triggers";
import { wellStateToRigFracStates } from "utils/map/rigFracValueMapping";

import { IPredicate, PropertyFilterModel } from "models/filter";

export type MainFilterRigFracAttribute = RigFracFocusAttribute | "stateProvince";

export interface MainFilterRigFracTrigger {
  property: string;
  attribute: MainFilterRigFracAttribute;
  needsTranslation: boolean;
  toRigFracValues?: (value: string) => string[];
}

const MAIN_FILTER_STATE_PROVINCE_PROPERTY = "Header.StateProvince";

const AZURE_STATE_PROVINCE_TRIGGER: MainFilterRigFracTrigger = {
  property: MAIN_FILTER_STATE_PROVINCE_PROPERTY,
  attribute: "stateProvince",
  needsTranslation: false
};

// AWS well headers carry "TX" where rig/frac carries "Texas" (EVA-338).
const AWS_STATE_PROVINCE_TRIGGER: MainFilterRigFracTrigger = {
  ...AZURE_STATE_PROVINCE_TRIGGER,
  toRigFracValues: wellStateToRigFracStates
};

export function getMainFilterRigFracTrigger(
  property: Maybe<string>
): Maybe<MainFilterRigFracTrigger> {
  if (property === MAIN_FILTER_STATE_PROVINCE_PROPERTY) {
    return IS_AWS_ENV ? AWS_STATE_PROVINCE_TRIGGER : AZURE_STATE_PROVINCE_TRIGGER;
  }
  return getFocusSyncTrigger(property);
}

export interface AttributeConstraint {
  include?: string[];
  exclude?: string[];
}

export type MainFilterRigFracConstraints = Partial<
  Record<MainFilterRigFracAttribute, AttributeConstraint>
>;

// Record literal keyed on MainFilterRigFracAttribute — exhaustive by
// construction, so adding a variant to the union without adding it here is a
// compile error, rather than a silently-skipped attribute on the two map-side
// consumers (buildRigFracFilter.ts, useRigFracLayerFilter.ts).
const MAIN_FILTER_RIGFRAC_ATTRIBUTES: Record<MainFilterRigFracAttribute, true> = {
  operator: true,
  basin: true,
  subBasin: true,
  county: true,
  stateProvince: true,
  country: true
};

export const MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER: readonly MainFilterRigFracAttribute[] =
  Object.keys(MAIN_FILTER_RIGFRAC_ATTRIBUTES) as MainFilterRigFracAttribute[];

export interface ResolveMainFilterRigFracParams {
  propertiesFilter: Maybe<readonly PropertyFilterModel[]>;
  translations: Readonly<Record<string, Maybe<ReadonlyMap<string, string>>>>;
  failedTranslations: ReadonlySet<string>;
  // Filter-independent translations of excluded values only. One value can map
  // to several public-record names (an operator filed under variants), and an
  // exclude must remove all of them.
  excludeTranslations?: Readonly<
    Record<string, Maybe<ReadonlyMap<string, readonly string[]>>>
  >;
}

export const EMPTY_MAIN_FILTER_RIGFRAC_CONSTRAINTS: MainFilterRigFracConstraints =
  Object.freeze({});

export function rawPredicateValues(predicate: IPredicate): string[] {
  const values = Array.isArray(predicate.value) ? predicate.value : [predicate.value];
  return values
    .map((value) => (value == null ? "" : String(value)).trim())
    .filter((value) => value.length > 0);
}

export type MainFilterRigFracDirection = "include" | "exclude";

export interface ClassifiedMainFilterPredicate {
  trigger: MainFilterRigFracTrigger;
  direction: MainFilterRigFracDirection;
}

/**
 * The single "does this predicate constrain a rig/frac attribute, and in which
 * direction" decision, shared by the resolver and by the hook that decides
 * which operator-name translations to request — so the two can never disagree
 * about which predicates count.
 */
export function classifyMainFilterPredicate(
  predicate: Maybe<IPredicate>
): Maybe<ClassifiedMainFilterPredicate> {
  if (!predicate || predicate.type !== PREDICATE_TYPE.Property) return undefined;
  // The Others rollup names no value, so it constrains no rig/frac attribute.
  if (predicate.rollup) return undefined;
  const trigger = getMainFilterRigFracTrigger(predicate.property);
  if (!trigger) return undefined;

  if (
    predicate.negatedOperator === true ||
    SUBTRACTIVE_OPERATORS.has(predicate.operator)
  ) {
    return { trigger, direction: "exclude" };
  }
  if (
    predicate.operator === PREDICATE_PROPERTY_OPERATOR.In ||
    predicate.operator === PREDICATE_PROPERTY_OPERATOR.Equals
  ) {
    return { trigger, direction: "include" };
  }
  return undefined;
}

// Keyed by property, so a translation that keeps failing across every
// re-render (this resolver runs inside a useMemo consumed by several mounted
// surfaces) warns Sentry once rather than on every recompute.
const warnedFailedTranslationProperties = new Set<string>();

function warnFailedTranslationOnce(property: string): void {
  if (warnedFailedTranslationProperties.has(property)) return;
  warnedFailedTranslationProperties.add(property);
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setContext("mainFilterRigFrac", { property });
    Sentry.captureMessage(
      "Main filter rig/frac: operator-name translation failed, leaving attribute unconstrained"
    );
  });
}

export function __resetMainFilterRigFracWarningsForTests(): void {
  warnedFailedTranslationProperties.clear();
}

export function resolveMainFilterRigFracConstraints(
  params: ResolveMainFilterRigFracParams
): MainFilterRigFracConstraints {
  const { propertiesFilter, translations, failedTranslations, excludeTranslations } =
    params;

  const includeSets = new Map<MainFilterRigFracAttribute, Set<string>>();
  const excludeSets = new Map<MainFilterRigFracAttribute, Set<string>>();

  // Selecting several values for one property writes a separate entry per
  // value, and a well matching any of them qualifies — so values sharing a
  // property union. Different properties are AND'd, so their resolved sets
  // intersect (only reachable for `operator`, which several properties map to).
  const includesByProperty = new Map<
    string,
    { attribute: MainFilterRigFracAttribute; values: Set<string> }
  >();

  // Checking Others keeps wells outside any named value, so that property constrains nothing.
  const othersCheckedProperties = new Set<string>();
  for (const entry of propertiesFilter ?? []) {
    for (const predicate of entry?.predicates ?? []) {
      if (predicate.rollup && !predicate.negatedOperator) {
        othersCheckedProperties.add(predicate.property);
      }
    }
  }

  for (const entry of propertiesFilter ?? []) {
    for (const predicate of entry?.predicates ?? []) {
      const classified = classifyMainFilterPredicate(predicate);
      if (!classified) continue;
      const { trigger, direction } = classified;
      const isExclusion = direction === "exclude";
      if (!isExclusion && othersCheckedProperties.has(predicate.property)) continue;

      let values = rawPredicateValues(predicate);

      if (trigger.needsTranslation) {
        // Deliberately fails OPEN, not closed like the empty-include case
        // below: a transient translation failure leaves this attribute
        // unconstrained rather than blanking every layer. That direction is
        // a product call that hasn't been made either way — warn so the
        // failure is visible instead of silently restoring the bug this
        // feature closes.
        if (failedTranslations.has(trigger.property)) {
          warnFailedTranslationOnce(trigger.property);
          continue;
        }
        const map = translations[trigger.property];
        if (!map) continue;
        const extra = isExclusion ? excludeTranslations?.[trigger.property] : undefined;
        values = values.flatMap((value) => {
          const key = normalizeMatchKey(value);
          const names = new Set(extra?.get(key));
          const scoped = map.get(key);
          if (scoped != null) names.add(scoped);
          return [...names];
        });
      }
      if (trigger.toRigFracValues) {
        values = values.flatMap(trigger.toRigFracValues);
      }

      // An exclude with nothing left (blank-only raw values, or every
      // translated value failing to map) contributes nothing; an include with
      // nothing left genuinely means "nothing qualifies" and must survive as
      // an empty include below. Applies the same way whether the predicate
      // needed translation or not.
      if (isExclusion && values.length === 0) continue;

      if (!isExclusion) {
        const existing = includesByProperty.get(trigger.property) ?? {
          attribute: trigger.attribute,
          values: new Set<string>()
        };
        for (const value of values) existing.values.add(value);
        includesByProperty.set(trigger.property, existing);
      } else {
        const existing = excludeSets.get(trigger.attribute) ?? new Set<string>();
        for (const value of values) existing.add(value);
        excludeSets.set(trigger.attribute, existing);
      }
    }
  }

  for (const { attribute, values } of includesByProperty.values()) {
    const existing = includeSets.get(attribute);
    if (existing == null) {
      includeSets.set(attribute, values);
      continue;
    }
    // Different properties resolving to the same attribute can spell the
    // same value differently (Header.Operator's "APACHE" vs a translated
    // "Apache" from Header.Operator_Short) — fold on the match key so they
    // still meet here, but keep a real spelling (the earliest property's) in
    // the output, since downstream matching needs an actual value.
    const existingByKey = new Map(
      [...existing].map((value) => [normalizeMatchKey(value), value])
    );
    const intersected = new Set<string>();
    for (const value of values) {
      const match = existingByKey.get(normalizeMatchKey(value));
      if (match != null) intersected.add(match);
    }
    includeSets.set(attribute, intersected);
  }

  const attributes = new Set<MainFilterRigFracAttribute>([
    ...includeSets.keys(),
    ...excludeSets.keys()
  ]);
  if (attributes.size === 0) return EMPTY_MAIN_FILTER_RIGFRAC_CONSTRAINTS;

  const result: MainFilterRigFracConstraints = {};
  for (const attribute of attributes) {
    const constraint: AttributeConstraint = {};
    const include = includeSets.get(attribute);
    if (include) constraint.include = [...include].sort();
    const exclude = excludeSets.get(attribute);
    if (exclude) constraint.exclude = [...exclude].sort();
    result[attribute] = constraint;
  }
  return result;
}
